import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton, SecondaryButton } from "./Buttons.jsx";
import ModalBasic from "./ModalBasic.jsx";
import { FaFingerprint, FaMobile, FaDesktop, FaShield } from "react-icons/fa6";
import { addToast, checkSession } from "../store/app/action.js";
import { beginWebAuthnRegistration, completeWebAuthnRegistration } from "../api/auth.js";

function WebAuthnSetupModal({ isOpen, onClose, onSuccess }) {
  const { user } = useSelector((state) => state.app);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Instructions, 2: Registration
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [passkeyName, setPasskeyName] = useState("");


  // Generate detailed device name
  const generateDeviceName = () => {
    const userAgent = navigator.userAgent;
    let deviceName = "Device";
    
    if (userAgent.includes('iPhone')) {
      deviceName = "iPhone";
    } else if (userAgent.includes('iPad')) {
      deviceName = "iPad";
    } else if (userAgent.includes('Android')) {
      deviceName = "Android Device";
    } else if (userAgent.includes('Mac')) {
      deviceName = "Mac";
    } else if (userAgent.includes('Windows')) {
      deviceName = "Windows PC";
    } else if (userAgent.includes('Linux')) {
      deviceName = "Linux PC";
    }
    
    // Add browser info
    let browser = "";
    if (userAgent.includes('Chrome')) {
      browser = " Chrome";
    } else if (userAgent.includes('Firefox')) {
      browser = " Firefox";
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browser = " Safari";
    } else if (userAgent.includes('Edge')) {
      browser = " Edge";
    }
    
    return `${deviceName}${browser} Passkey`;
  };

  // Initialize passkey name when modal opens
  useEffect(() => {
    if (isOpen && !passkeyName) {
      setPasskeyName(generateDeviceName());
    }
  }, [isOpen, passkeyName]);

  const handleClose = () => {
    setError(null);
    setStep(1);
    setLoading(false);
    setIsRegistering(false);
    setRegistrationSuccess(false);
    setPasskeyName("");
    onClose();
  };

  const checkWebAuthnSupport = () => {
    if (!window.PublicKeyCredential) {
      setError("WebAuthn is not supported in this browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge.");
      return false;
    }
    return true;
  };

  const registerPasskey = async () => {
    if (!checkWebAuthnSupport()) {
      return;
    }

    try {
      setIsRegistering(true);
      setError(null);

      
      // Step 1: Begin registration with backend
      const beginResponse = await beginWebAuthnRegistration();
      
      if (beginResponse.status !== "success") {
        throw new Error(beginResponse.message || "Failed to begin WebAuthn registration");
      }


      // Step 2: Create credential using WebAuthn API
      // Convert base64 challenge to ArrayBuffer
      const challengeData = beginResponse.challenge.publicKey;
      
      // Handle challenge - might be base64 or already an ArrayBuffer
      let challengeArrayBuffer;
      if (typeof challengeData.challenge === 'string') {
        try {
          // Try base64 decoding first
          challengeArrayBuffer = Uint8Array.from(atob(challengeData.challenge), c => c.charCodeAt(0)).buffer;
        } catch (e) {
          // If base64 fails, try treating as raw string
          challengeArrayBuffer = new TextEncoder().encode(challengeData.challenge).buffer;
        }
      } else {
        challengeArrayBuffer = challengeData.challenge;
      }
      
      // Handle user ID - might be base64 or already an ArrayBuffer
      let userIdArrayBuffer;
      if (typeof challengeData.user.id === 'string') {
        try {
          // Try base64 decoding first
          userIdArrayBuffer = Uint8Array.from(atob(challengeData.user.id), c => c.charCodeAt(0)).buffer;
        } catch (e) {
          // If base64 fails, try treating as raw string
          userIdArrayBuffer = new TextEncoder().encode(challengeData.user.id).buffer;
        }
      } else {
        userIdArrayBuffer = challengeData.user.id;
      }
      
      const publicKeyOptions = {
        ...challengeData,
        challenge: challengeArrayBuffer,
        user: {
          ...challengeData.user,
          id: userIdArrayBuffer,
          // Ensure user name is set for the system prompt
          name: challengeData.user.name || challengeData.user.displayName || user?.first_name || user?.email || "User"
        }
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions
      });


      // Step 3: Complete registration with backend
      const credentialName = passkeyName || generateDeviceName();
      
      const completeResponse = await completeWebAuthnRegistration(credential, credentialName);
      
      if (completeResponse.status !== "success") {
        throw new Error(completeResponse.message || "Failed to complete WebAuthn registration");
      }


      setRegistrationSuccess(true);
      setIsRegistering(false);
      
      // Passkey registered successfully - no toast needed as user will see the updated UI
      
      // Refresh user data to get updated MFA status
      dispatch(checkSession());
      
      // Auto-close after success
      setTimeout(() => {
        handleSuccess();
      }, 2000);

    } catch (err) {
      setIsRegistering(false);
      
      if (err.name === "NotAllowedError") {
        setError("Registration was cancelled or not allowed. Please try again and make sure to complete the authentication when prompted.");
      } else if (err.name === "NotSupportedError") {
        setError("Your device doesn't support passkeys. Please use a device with Touch ID, Face ID, Windows Hello, or a security key.");
      } else if (err.name === "SecurityError") {
        setError("Security error occurred. Please make sure you're using HTTPS and try again.");
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Failed to register passkey. Please try again.");
      }
    }
  };

  const handleSuccess = () => {
    setError(null);
    setStep(1);
    setIsRegistering(false);
    setRegistrationSuccess(false);
    setPasskeyName("");
    if (onSuccess) {
      onSuccess();
    }
  };

  
  return (
    <ModalBasic
      id="webauthn-setup-modal"
      modalOpen={isOpen}
      setModalOpen={handleClose}
      width={"2xl"}
    >
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/50 mx-auto mb-4">
            <FaFingerprint className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 text-center mb-2">
            Set Up Passkey
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-center">
            Use your device's built-in security features for secure authentication.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-center">{error}</p>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            {/* Device Support Info */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
              <h3 className="font-medium text-slate-900 dark:text-slate-100 mb-3">
                Supported Devices & Methods:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <FaMobile className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Mobile Devices</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Touch ID, Face ID, Fingerprint</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                    <FaDesktop className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Computers</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Windows Hello, Touch ID (Mac)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-3">
              <h3 className="font-medium text-slate-900 dark:text-slate-100">Why use a passkey?</h3>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2 list-disc list-inside">
                <li>More secure than passwords - uses cryptographic keys</li>
                <li>Faster than typing codes - just use your fingerprint or face</li>
                <li>Works across your devices when signed into the same account</li>
                <li>Resistant to phishing attacks</li>
              </ul>
            </div>

            {/* Instructions */}
            <div className="space-y-3">
              <h3 className="font-medium text-slate-900 dark:text-slate-100">Setup Instructions:</h3>
              <ol className="text-sm text-slate-600 dark:text-slate-400 space-y-2 list-decimal list-inside">
                <li>Make sure your device supports biometric authentication</li>
                <li>Ensure you're signed into your device with your main account</li>
                <li>Click "Create Passkey" below</li>
                <li>Follow your device's prompts to authenticate</li>
                <li>Your passkey will be saved securely</li>
              </ol>
            </div>

            {/* Passkey Name Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Passkey Name
              </label>
              <input
                type="text"
                value={passkeyName}
                onChange={(e) => setPasskeyName(e.target.value)}
                placeholder="Enter a name for this passkey"
                className="form-input w-full"
                disabled={loading}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Choose a name to help you identify this passkey (e.g., "Work MacBook", "Personal iPhone")
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <SecondaryButton onClick={handleClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={() => setStep(2)}>
                Create Passkey
              </PrimaryButton>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {registrationSuccess ? "Passkey Created Successfully!" : "Creating Your Passkey"}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {registrationSuccess 
                  ? "Your passkey has been registered and is ready to use."
                  : "Follow your device's prompts to create a secure passkey."
                }
              </p>
            </div>

            {isRegistering && (
              <div className="flex items-center justify-center p-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                  <p className="text-slate-500 dark:text-slate-400">Creating passkey...</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                    Complete the authentication on your device
                  </p>
                </div>
              </div>
            )}

            {registrationSuccess && (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaShield className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Your passkey is now set up and ready to use for secure authentication.
                </p>
              </div>
            )}

            {!registrationSuccess && !isRegistering && (
              <div className="flex justify-end space-x-3">
                <SecondaryButton onClick={() => setStep(1)}>
                  Back
                </SecondaryButton>
                <PrimaryButton onClick={registerPasskey}>
                  Create Passkey
                </PrimaryButton>
              </div>
            )}

            {registrationSuccess && (
              <div className="flex justify-end space-x-3">
                <PrimaryButton onClick={handleSuccess}>
                  Complete Setup
                </PrimaryButton>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalBasic>
  );
}

export default WebAuthnSetupModal;
